import type { TaskColor } from '@obras/shared';
import { TASK_COLOR_LABELS, TASK_COLORS } from '@obras/shared';
import { Ban, Check } from 'lucide-react';

import { cn } from '../../lib/cn.js';
import { FLAG_BG } from './styles.js';

/**
 * Escolha da etiqueta de cor.
 *
 * Seis círculos e a opção de nenhuma. Nada de seletor de cor livre: paleta
 * fechada é o que mantém o quadro legível quando dez pessoas escolherem, e
 * garante o contraste que a tela precisa em campo.
 *
 * A escolhida ganha um **visto**, não um anel. Anel disputaria o mesmo pixel
 * do anel de foco, e cor sozinha não pode ser o único portador da informação
 * (design.md §4) — quem não distingue os tons ficaria sem saber qual está
 * marcada.
 */
export function ColorPicker({
  value,
  onChange,
}: {
  value: TaskColor | null;
  onChange: (color: TaskColor | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium">Etiqueta</span>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-pressed={value === null}
          aria-label="Sem etiqueta"
          title="Sem etiqueta"
          className="grid size-7 place-items-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent"
        >
          {value === null ? (
            <Check size={14} strokeWidth={2.5} className="text-foreground" aria-hidden />
          ) : (
            <Ban size={13} strokeWidth={1.75} aria-hidden />
          )}
        </button>

        {TASK_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            aria-pressed={value === color}
            aria-label={TASK_COLOR_LABELS[color]}
            title={TASK_COLOR_LABELS[color]}
            className={cn('grid size-7 place-items-center rounded-full', FLAG_BG[color])}
          >
            {value === color ? (
              <Check size={14} strokeWidth={2.5} className="text-white" aria-hidden />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
